import { useEffect, useState } from "react";
import GenerateProposalModal from "./GenerateProposalModal";
import OffersModal from "./OffersModal";
import OffersInssModal from "./OffersInssModal";
import PersonalInfoModal from "./PersonalInfoModal";
import UploadFilesModal from "./UploadFilesModal";
import ProposalReviewModal from "./ProposalReviewModal";
import ProposalSubmittedModal from "./ProposalSubmittedModal";
import { useSetAtom } from "jotai";
import { currentStep } from "@/app/store";
import { PartnerType } from "@/app/utils/partners.enum";
import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { useRegisterSupportContext } from "@/app/store/supportScreenContext";

type step =
  | "generate"
  | "offers"
  | "personalInfo"
  | "uploadFiles"
  | "proposalReview"
  | "proposalSubmitted";

const STEP_LABEL: Record<step, string> = {
  generate: "Geração de proposta",
  offers: "Ofertas",
  personalInfo: "Dados pessoais",
  uploadFiles: "Envio de documentos",
  proposalReview: "Revisão da proposta",
  proposalSubmitted: "Proposta enviada",
};

interface CrefisaModalProps {
  onBack: () => void;
  onClose?: () => void;
  partnerType?: PartnerType.CREFISA | PartnerType.CREFISA_INSS;
}

export default function CrefisaModal({
  onBack,
  onClose,
  partnerType = PartnerType.CREFISA,
}: CrefisaModalProps) {
  const [step, setStep] = useState<step>("generate");
  const [personalInfoBack, setPersonalInfoBack] = useState<step>("offers");
  const setPersonalInfoStep = useSetAtom(currentStep);
  const { setPartnerType, customerData, simulationData, proposalData } =
    useCrefisaContext();

  useRegisterSupportContext("simulation.crefisa", {
    product: partnerType,
    step: `${STEP_LABEL[step]} ${partnerType === PartnerType.CREFISA_INSS ? "Crefisa INSS" : "Crefisa"}`,
    data: {
      customer: customerData,
      simulation: simulationData,
      proposal: proposalData,
    },
  });

  const isInss = partnerType === PartnerType.CREFISA_INSS;

  useEffect(() => {
    setPartnerType(partnerType);
  }, [partnerType, setPartnerType]);

  const goToPersonalInfo = (from: step) => {
    setPersonalInfoStep(0);
    setPersonalInfoBack(from);
    setStep("personalInfo");
  };

  if (step === "generate") {
    return (
      <GenerateProposalModal
        partnerType={partnerType}
        onBack={onBack}
        onContinue={() => setStep("offers")}
        onContinuePreApproved={() => goToPersonalInfo("generate")}
      />
    );
  }

  if (step === "offers") {
    if (isInss) {
      return (
        <OffersInssModal
          onBack={() => setStep("generate")}
          onContinue={() => goToPersonalInfo("offers")}
        />
      );
    }

    return (
      <OffersModal
        onBack={() => setStep("generate")}
        onContinue={() => goToPersonalInfo("offers")}
      />
    );
  }

  if (step === "personalInfo") {
    return (
      <PersonalInfoModal
        onBack={() => setStep(personalInfoBack)}
        onContinue={() => setStep("uploadFiles")}
      />
    );
  }

  if (step === "uploadFiles") {
    return (
      <UploadFilesModal
        onBack={() => setStep("personalInfo")}
        onContinue={() => setStep("proposalReview")}
      />
    );
  }

  if (step === "proposalReview") {
    return (
      <ProposalReviewModal
        onBack={() => goToPersonalInfo("offers")}
        onContinue={() => setStep("proposalSubmitted")}
      />
    );
  }

  if (step === "proposalSubmitted") {
    return <ProposalSubmittedModal onClose={onClose} />;
  }

  return null;
}
